// public/storage-manager.js
// Centralized LocalStorage management with LRU eviction and size limits

const MAX_STORAGE_SIZE_MB = 4; // Conservative limit (browsers typically allow 5-10MB)
const MAX_STORAGE_BYTES = MAX_STORAGE_SIZE_MB * 1024 * 1024;
const CLEANUP_THRESHOLD = 0.8; // Trigger cleanup at 80% capacity

// Metadata tracking for LRU eviction
const METADATA_KEY = 'storageMetadata_v1';
const METADATA_DEBOUNCE_MS = 100; // Batch metadata saves within this window

// In-memory metadata cache to reduce localStorage reads/writes
let metadataCache = null;
let metadataDirty = false;
let metadataSaveTimer = null;

/**
 * Get storage metadata (access times, sizes)
 * Uses in-memory cache to avoid repeated localStorage reads
 */
function getMetadata() {
  if (metadataCache !== null) {
    return metadataCache;
  }

  try {
    const data = localStorage.getItem(METADATA_KEY);
    metadataCache = data ? JSON.parse(data) : {};
    return metadataCache;
  } catch (e) {
    console.warn('[Storage] Failed to parse metadata:', e);
    metadataCache = {};
    return metadataCache;
  }
}

/**
 * Mark metadata as dirty and schedule a debounced save
 * This batches multiple metadata updates into a single localStorage write
 */
function scheduleMetadataSave() {
  metadataDirty = true;

  // Clear existing timer
  if (metadataSaveTimer) {
    clearTimeout(metadataSaveTimer);
  }

  // Schedule save after debounce period
  metadataSaveTimer = setTimeout(() => {
    flushMetadata();
  }, METADATA_DEBOUNCE_MS);
}

/**
 * Immediately flush metadata to localStorage if dirty
 */
function flushMetadata() {
  if (!metadataDirty || metadataCache === null) {
    return;
  }

  try {
    localStorage.setItem(METADATA_KEY, JSON.stringify(metadataCache));
    metadataDirty = false;
  } catch (e) {
    console.error('[Storage] Failed to save metadata:', e);
  }
}

/**
 * Save storage metadata (debounced)
 * Updates in-memory cache immediately, batches localStorage writes
 */
function saveMetadata(metadata) {
  metadataCache = metadata;
  scheduleMetadataSave();
}

/**
 * Save storage metadata immediately (for critical operations like eviction)
 */
function saveMetadataImmediate(metadata) {
  metadataCache = metadata;
  metadataDirty = true;
  flushMetadata();
}

/**
 * Estimate size of localStorage item in bytes
 */
function estimateItemSize(key, value) {
  // UTF-16 encoding: 2 bytes per character
  const keySize = key.length * 2;
  const valueSize = value.length * 2;
  return keySize + valueSize;
}

/**
 * Get total localStorage usage in bytes
 */
function getTotalSize() {
  let total = 0;
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        const value = localStorage.getItem(key) || '';
        total += estimateItemSize(key, value);
      }
    }
  } catch (e) {
    console.error('[Storage] Error calculating size:', e);
  }
  return total;
}

/**
 * LRU eviction - removes least recently used items
 */
function performLRUEviction(targetBytes) {
  const metadata = getMetadata();
  const entries = [];

  // Collect all managed entries with their access times
  for (const [key, meta] of Object.entries(metadata)) {
    // Check if item still exists in localStorage
    if (localStorage.getItem(key) !== null) {
      entries.push({
        key,
        lastAccess: meta.lastAccess || 0,
        size: meta.size || 0,
      });
    }
  }

  // Sort by last access time (oldest first)
  entries.sort((a, b) => a.lastAccess - b.lastAccess);

  let freedBytes = 0;
  const keysToRemove = [];

  // Remove items until we've freed enough space
  for (const entry of entries) {
    keysToRemove.push(entry.key);
    freedBytes += entry.size;

    if (freedBytes >= targetBytes) {
      break;
    }
  }

  // Actually remove the items
  for (const key of keysToRemove) {
    console.log(`[Storage] Evicting LRU item: ${key}`);
    localStorage.removeItem(key);
    delete metadata[key];
  }

  saveMetadataImmediate(metadata);
  return freedBytes;
}

/**
 * Write to localStorage; on QuotaExceededError free 50% and retry once.
 * A second failure (or any other error) propagates to the caller.
 */
function writeWithEviction(key, valueString) {
  try {
    localStorage.setItem(key, valueString);
  } catch (e) {
    if (e.name !== 'QuotaExceededError') throw e;
    console.error('[Storage] Quota exceeded, attempting emergency cleanup');
    performLRUEviction(MAX_STORAGE_BYTES * 0.5);
    localStorage.setItem(key, valueString);
  }
}

/**
 * Set item with LRU tracking and automatic cleanup
 */
export function setStorageItem(key, value, options = {}) {
  const { skipSizeCheck = false, expiresInDays = null } = options;

  try {
    const valueString = typeof value === 'string' ? value : JSON.stringify(value);
    const itemSize = estimateItemSize(key, valueString);

    // Check if we need to free up space
    if (!skipSizeCheck) {
      const currentSize = getTotalSize();
      const projectedSize = currentSize + itemSize;

      // If we're over threshold, perform cleanup
      if (projectedSize > MAX_STORAGE_BYTES * CLEANUP_THRESHOLD) {
        console.warn(
          `[Storage] Approaching limit (${(projectedSize / 1024 / 1024).toFixed(2)}MB / ${MAX_STORAGE_SIZE_MB}MB)`
        );

        // Calculate how much to free (at least 20% of max, or enough for the new item)
        const targetFree = Math.max(
          MAX_STORAGE_BYTES * 0.2,
          projectedSize - MAX_STORAGE_BYTES * 0.6
        );

        performLRUEviction(targetFree);
      }
    }

    writeWithEviction(key, valueString);

    // Update metadata
    const metadata = getMetadata();
    metadata[key] = {
      lastAccess: Date.now(),
      size: itemSize,
      expires: expiresInDays ? Date.now() + expiresInDays * 24 * 60 * 60 * 1000 : null,
    };
    saveMetadata(metadata);

    return true;
  } catch (e) {
    console.error('[Storage] Error setting item:', e);
    return false;
  }
}

/**
 * Get item and update access time
 */
export function getStorageItem(key, options = {}) {
  const { parseJSON = true, updateAccessTime = true } = options;

  try {
    const value = localStorage.getItem(key);
    if (value === null) return null;

    // Check expiration
    const metadata = getMetadata();
    const itemMeta = metadata[key];

    if (itemMeta?.expires && Date.now() > itemMeta.expires) {
      console.log(`[Storage] Item expired: ${key}`);
      localStorage.removeItem(key);
      delete metadata[key];
      saveMetadata(metadata);
      return null;
    }

    // Update access time for LRU
    if (updateAccessTime && itemMeta) {
      itemMeta.lastAccess = Date.now();
      metadata[key] = itemMeta;
      saveMetadata(metadata);
    }

    return parseJSON ? tryParseJSON(value) : value;
  } catch (e) {
    console.error('[Storage] Error getting item:', e);
    return null;
  }
}

/**
 * Remove item and clean up metadata
 */
export function removeStorageItem(key) {
  try {
    localStorage.removeItem(key);

    const metadata = getMetadata();
    delete metadata[key];
    saveMetadata(metadata);
  } catch (e) {
    console.error('[Storage] Error removing item:', e);
  }
}

/**
 * Try to parse JSON, return original value if fails
 */
function tryParseJSON(value) {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
}

/**
 * Clean up expired items
 */
export function cleanupExpiredItems() {
  const metadata = getMetadata();
  const now = Date.now();
  let cleanedCount = 0;

  for (const [key, meta] of Object.entries(metadata)) {
    if (meta.expires && now > meta.expires) {
      console.log(`[Storage] Removing expired item: ${key}`);
      localStorage.removeItem(key);
      delete metadata[key];
      cleanedCount++;
    }
  }

  if (cleanedCount > 0) {
    console.log(`[Storage] Cleaned up ${cleanedCount} expired items`);
    saveMetadata(metadata);
  }

  return cleanedCount;
}

/**
 * Get storage statistics
 */
export function getStorageStats() {
  const totalSize = getTotalSize();
  const metadata = getMetadata();
  const itemCount = Object.keys(metadata).length;

  return {
    totalSizeBytes: totalSize,
    totalSizeMB: (totalSize / 1024 / 1024).toFixed(2),
    maxSizeMB: MAX_STORAGE_SIZE_MB,
    usagePercent: ((totalSize / MAX_STORAGE_BYTES) * 100).toFixed(1),
    itemCount,
    itemsManaged: itemCount,
  };
}

/**
 * Initialize - run cleanup on app load
 */
export function initializeStorageManager() {
  console.log('[Storage] Initializing storage manager');

  // Clean up expired items
  cleanupExpiredItems();

  // Log stats
  const stats = getStorageStats();
  console.log(
    `[Storage] Usage: ${stats.totalSizeMB}MB / ${stats.maxSizeMB}MB (${stats.usagePercent}%) - ${stats.itemCount} items`
  );

  // If we're over 90%, do proactive cleanup
  if (parseFloat(stats.usagePercent) > 90) {
    console.warn('[Storage] Over 90% capacity, performing proactive cleanup');
    performLRUEviction(MAX_STORAGE_BYTES * 0.3); // Free 30%
  }
}

// For debugging (console-only; declared on Window in src/lib/globals.d.ts)
export const debugStorage = {
  stats: getStorageStats,
  cleanup: cleanupExpiredItems,
  evict: performLRUEviction,
  metadata: getMetadata,
};
window.debugStorage = debugStorage;
